import {Box, Chip, Fade, useMediaQuery} from "@mui/material";
import {useCallback, useEffect, useRef, useState} from "react";
import {PANEL_SLIDE_TRANSITION} from "./useNavLayout";
import {stopCameraSweep, stopHouseTour, SWEEP_DURATION_MS, sweepCamera} from "../../common";

const TOUCH_POINTER_QUERY = "(pointer: coarse)";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const HINT_INSET_PX = 16;

// MUI's own entering duration, with a slower exit so the pill reads as leaving rather than blinking off.
const FADE_MS = {enter: 225, exit: 800};

// Long enough for the camera to have settled where the panorama wants it before the sweep takes effect.
const SWEEP_START_DELAY_MS = 600;

// The movement takes the attention while it lasts, so keep the verbal hint visible for a while after the sweep.
const LINGER_AFTER_SWEEP_MS = 2000;

export interface LookAroundHintProps {
    /** Height of what the layout already occupies at the bottom, which the hint sits above. */
    bottomOffsetPx: number;
    /** Width taken by the navigation panel, so the hint centres on the panorama and not on the viewport. */
    rightOffsetPx: number;
    /** Called when the intro is over, whether it ran its course or the visitor cut it short. */
    onFinished: () => void;
}

/**
 * Teaches the viewer how to look around in the panorama by showing a visual hint and a short camera sweep.
 */
export function LookAroundHint(props: LookAroundHintProps) {
    const {bottomOffsetPx, rightOffsetPx, onFinished} = props;

    const [isVisible, setIsVisible] = useState<boolean>(true);
    const isTouch = useMediaQuery(TOUCH_POINTER_QUERY);
    const prefersReducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);

    // Held in a ref so the timers below do not start over should the caller pass a new function each render.
    const onFinishedRef = useRef(onFinished);
    onFinishedRef.current = onFinished;

    // Held in refs so that ending the intro early can call the rest of it off, wherever it is called from.
    const sweepStartTimer = useRef<ReturnType<typeof setTimeout>>();
    const hintEndTimer = useRef<ReturnType<typeof setTimeout>>();

    const finishIntro = useCallback(() => {
        // A visitor can take over before the sweep has even started, and a sweep landing after that would
        // pull the panorama out of their hands.
        clearTimeout(sweepStartTimer.current);
        clearTimeout(hintEndTimer.current);

        setIsVisible(false);
        onFinishedRef.current();
    }, []);

    useEffect(() => {
        // The camera stays put where motion is unwelcome, and the pill has that much less to wait out.
        if (!prefersReducedMotion) {
            sweepStartTimer.current = setTimeout(sweepCamera, SWEEP_START_DELAY_MS);
        }

        const sweptForMs = prefersReducedMotion ? 0 : SWEEP_DURATION_MS;
        hintEndTimer.current = setTimeout(finishIntro,
            SWEEP_START_DELAY_MS + sweptForMs + LINGER_AFTER_SWEEP_MS);

        return () => {
            clearTimeout(sweepStartTimer.current);
            clearTimeout(hintEndTimer.current);
        };
    }, [prefersReducedMotion, finishIntro]);

    // A-Frame creates the canvas after this mounts, so the listener goes on the window and asks what was
    // pressed rather than waiting for the canvas to appear.
    useEffect(() => {
        const yieldToVisitor = (event: Event) => {
            if (!(event.target as Element)?.classList?.contains("a-canvas")) {
                return;
            }

            finishIntro();
            stopCameraSweep();

            // Someone dragging is not an idle viewer, so the exhibition house tour is called off.
            stopHouseTour();
        };

        window.addEventListener("pointerdown", yieldToVisitor);

        return () => window.removeEventListener("pointerdown", yieldToVisitor);
    }, [finishIntro]);

    return <Box role={"status"} aria-live={"polite"} sx={{
        pointerEvents: "none",
        position: "absolute",
        bottom: bottomOffsetPx + HINT_INSET_PX,
        left: 0,
        right: rightOffsetPx,
        display: "flex",
        justifyContent: "center",
        transition: `right ${PANEL_SLIDE_TRANSITION}`
    }}>
        <Fade in={isVisible} appear unmountOnExit timeout={FADE_MS}>
            <Chip label={isTouch ? "↔ Swipe to look around" : "↔ Drag to look around"}
                  sx={{backgroundColor: "rgba(0, 0, 0, 0.7)", color: "common.white"}}/>
        </Fade>
    </Box>;
}
